'use client';

import { useState } from 'react';
import { UpdateFeedEntry, VersionsInterface } from 'types/common';
import MdParser from '#/components/md-parser';
import Link from 'next/link';
import { Button } from '#/components/ui/button';
import { isPublishableVersion, versionPath, versionSlug } from '#/lib/update-logs';
import UpdateFeed from './feed';

// 사이드바에서 "최근 변경" 뷰를 가리키는 예약 키 (버전 문자열과 충돌하지 않음)
const RECENT = '__recent__';

interface UpdateLogsClientProps {
  versions: VersionsInterface[];
  feed: UpdateFeedEntry[];
  // 'recent' 면(?view=recent) 피드가 비어도 최근 변경 뷰로 진입
  initialView?: 'recent';
}

export default function UpdateLogsClient({
  versions,
  feed,
  initialView
}: UpdateLogsClientProps) {
  const [selected, setSelected] = useState(
    initialView === 'recent' || feed.length > 0
      ? RECENT
      : (versions[0]?.version ?? RECENT)
  );
  const current = versions.find((ver) => ver.version === selected);

  return (
    <div className="flex flex-col sm:flex-row gap-4 sm:gap-6 md:gap-10">
      <div className="flex flex-col shrink-0">
        <p className="px-4 pb-1 text-xs font-medium text-muted-foreground">
          서비스 업데이트
        </p>
        <Button
          variant={'ghost'}
          size={'lg'}
          onClick={() => {
            setSelected(RECENT);
          }}
          className={`${selected === RECENT ? 'text-[#104573]' : ''}`}
        >
          최근 변경
        </Button>
        <p className="px-4 pt-4 pb-1 text-xs font-medium text-muted-foreground">
          공식 릴리즈
        </p>
        {versions.map((ver, inx) => (
          <Button
            variant={'ghost'}
            size={'lg'}
            key={inx}
            onClick={() => {
              setSelected(ver.version);
            }}
            className={`${selected === ver.version ? 'text-[#104573]' : ''}`}
          >
            {ver.version}
          </Button>
        ))}
      </div>
      <div className="min-w-0 flex-1">
        {selected === RECENT ? (
          <UpdateFeed entries={feed} />
        ) : (
          <div id={current ? versionSlug(current.version) : undefined}>
            {current && isPublishableVersion(current) && (
              <Link
                href={versionPath(current.version)}
                className="mb-2 inline-block text-xs text-muted-foreground underline"
              >
                이 버전만 보기 (고정 주소)
              </Link>
            )}
            <MdParser content={current?.content ?? ''} />
          </div>
        )}
      </div>
    </div>
  );
}
